/**
 * 効果音エンジン（自前・GPL非依存・Web Audio API でサンプル再生）。
 *
 * 音源は public/sounds/*.ogg（tailuge/billiards 由来・GPL-3.0・本アプリと同一ライセンス）。
 * 各イベントの音を衝突速度 [m/s] に応じて
 *   - 音量（gain）
 *   - 再生レート（playbackRate＝ピッチ。速い衝突ほど高く硬い音）
 * で変化させ、さらに毎回わずかにデチューンして機械的な反復感を避ける。
 *
 * ロードの二段構え（起動直後に無音／初撞で遅延 を防ぐ）:
 *   1) 生成時に音源ファイルを fetch（arrayBuffer）して先読み。AudioContext 不要。
 *   2) 最初の resume()（＝ユーザー操作）で AudioContext を生成し、先読み済み
 *      arrayBuffer を decodeAudioData する（デコードは速い）。
 * ブラウザの自動再生制限のため、AudioContext の生成・resume はユーザー操作起点で行う。
 * ロード未完 / 非対応環境 / 設定OFF では無音で握りつぶす（例外を投げない）。
 *
 * 本モジュールはプロジェクト内の他モジュール（特に GPL の engine）に一切依存しない。
 */

type AudioCtxCtor = typeof AudioContext;

export interface SoundEngine {
  /** AudioContext を生成/再開し、先読み済み音源をデコードする（初回ユーザー操作で呼ぶ）。冪等。 */
  resume: () => void;
  /** ショット音（キュー→手球）。speed=手球の初速 [m/s]。 */
  cue: (speed: number) => void;
  /** 球同士の衝突音。speed=衝突の相対法線速度 [m/s]。 */
  collision: (speed: number) => void;
  /** ポケットイン音。speed=落下時の速度 [m/s]。 */
  pocket: (speed: number) => void;
}

// 効果音サンプル（public/sounds 配下）。キーは内部識別子。
// クッション反射音は遅延の違和感が強かったため廃止（cushion.ogg は同梱しない）。
const SAMPLE_FILES = {
  cue: 'cue.ogg',
  collision: 'ballcollision.ogg',
  pot: 'pot.ogg',
} as const;
type SampleKey = keyof typeof SAMPLE_FILES;
const SAMPLE_KEYS = Object.keys(SAMPLE_FILES) as SampleKey[];

// 全体音量（マスター）。
const MASTER_GAIN = 0.9;
// 同時発音数の上限（ブレイクで多数の衝突が同時発生しても破綻させない）。
const MAX_VOICES = 16;
// これ未満の速度のイベントは微小すぎるので鳴らさない（数値ジッタ除去）。
const MIN_SPEED = 0.06;

export const createSoundEngine = (): SoundEngine => {
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let voices = 0;
  // 先読み（fetch 済み・未デコード）の arrayBuffer と、デコード済み AudioBuffer。
  const fetched = new Map<SampleKey, ArrayBuffer>();
  const decoded = new Map<SampleKey, AudioBuffer>();
  // 各サンプルの音の立ち上がり位置 [s]。先頭の無音/緩い立ち上がりを飛ばして再生し、
  // 衝突の瞬間と音のズレ（特にクッションの違和感）を抑える。
  const onset = new Map<SampleKey, number>();
  let prefetchStarted = false;

  // 波形の立ち上がり位置 [s] を求める（最初に振幅がピークの一定割合を超える点）。
  const detectOnset = (buf: AudioBuffer): number => {
    const data = buf.getChannelData(0);
    let peak = 0;
    for (let i = 0; i < data.length; i++) {
      const a = Math.abs(data[i] ?? 0);
      if (a > peak) peak = a;
    }
    if (peak <= 0) return 0;
    const thr = Math.max(0.02, peak * 0.12);
    for (let i = 0; i < data.length; i++) {
      if (Math.abs(data[i] ?? 0) >= thr) {
        // 1.5ms だけ手前から鳴らしてアタックの頭が欠けないようにする。
        return Math.max(0, i / buf.sampleRate - 0.0015);
      }
    }
    return 0;
  };

  // AudioContext コンストラクタを取得（webkit 接頭辞も考慮）。
  const getCtor = (): AudioCtxCtor | undefined => {
    if (typeof AudioContext !== 'undefined') return AudioContext;
    const w = window as { webkitAudioContext?: AudioCtxCtor };
    return w.webkitAudioContext;
  };

  // public 配下の絶対 URL（サブパス配信でも document.baseURI で解決）。
  const sampleUrl = (file: string): string =>
    new URL(`sounds/${file}`, document.baseURI).href;

  // 1 件デコード（AudioContext が出来てから／arrayBuffer が揃ってから）。
  // decodeAudioData は ArrayBuffer を detach し得るため slice(0) でコピーを渡す。
  const decodeOne = (key: SampleKey, c: AudioContext): void => {
    if (decoded.has(key)) return;
    const arr = fetched.get(key);
    if (!arr) return;
    void (async (): Promise<void> => {
      try {
        const buf = await c.decodeAudioData(arr.slice(0));
        decoded.set(key, buf);
        onset.set(key, detectOnset(buf));
      } catch (err) {
        console.error(`SoundEngine: ${SAMPLE_FILES[key]} のデコードに失敗:`, err);
      }
    })();
  };

  // 起動時に音源を先読み（fetch）。AudioContext は不要。冪等。
  const prefetch = (): void => {
    if (prefetchStarted) return;
    prefetchStarted = true;
    for (const key of SAMPLE_KEYS) {
      void (async (): Promise<void> => {
        try {
          const res = await fetch(sampleUrl(SAMPLE_FILES[key]));
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          fetched.set(key, await res.arrayBuffer());
          if (ctx) decodeOne(key, ctx); // 既にコンテキストがあれば即デコード
        } catch (err) {
          console.error(`SoundEngine: ${SAMPLE_FILES[key]} の読み込みに失敗:`, err);
        }
      })();
    }
  };

  const resume = (): void => {
    try {
      if (!ctx) {
        const Ctor = getCtor();
        if (!Ctor) return; // 非対応環境では無音で続行
        // 出力レイテンシ最小化（衝突音と描画のズレを抑える）。
        ctx = new Ctor({ latencyHint: 'interactive' });
        master = ctx.createGain();
        master.gain.value = MASTER_GAIN;
        master.connect(ctx.destination);
      }
      if (ctx.state === 'suspended') void ctx.resume();
      // 先読み済みのものをデコード（fetch 未完のものは prefetch 側で後追いデコード）。
      for (const key of SAMPLE_KEYS) decodeOne(key, ctx);
    } catch (err) {
      console.error('SoundEngine: AudioContext 初期化に失敗:', err);
      ctx = null;
      master = null;
    }
  };

  // 速度 [0,ref] を [0,1] の知覚的レベルへ（sqrt で小音も聞こえやすく）。
  const level = (speed: number, ref: number): number =>
    Math.sqrt(Math.min(1, Math.max(0, speed) / ref));

  /**
   * サンプルを 1 回再生。
   *   gain   = 音量（0..1 程度）
   *   rate   = 再生レート（ピッチ。1=原音）
   *   detune = ±この割合でランダムにレートを揺らす（反復感の除去）
   */
  const play = (
    key: SampleKey,
    gain: number,
    rate: number,
    detune: number,
  ): void => {
    if (!ctx || !master || voices >= MAX_VOICES) return;
    const buf = decoded.get(key);
    if (!buf) return; // ロード未完 / 失敗時は無音
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = Math.max(
      0.25,
      rate * (1 + (Math.random() * 2 - 1) * detune),
    );
    const g = ctx.createGain();
    g.gain.value = Math.max(0, gain);
    src.connect(g);
    g.connect(master);
    voices++;
    src.onended = (): void => {
      voices = Math.max(0, voices - 1);
    };
    // 立ち上がり位置から再生（先頭の無音/緩い onset をスキップして遅延感を抑える）。
    src.start(ctx.currentTime, onset.get(key) ?? 0);
  };

  const cue = (speed: number): void => {
    if (speed < MIN_SPEED) return;
    const v = level(speed, 6);
    play('cue', 0.5 + v * 0.5, 0.95 + v * 0.18, 0.03);
  };

  const collision = (speed: number): void => {
    if (speed < MIN_SPEED) return;
    const v = level(speed, 4);
    play('collision', 0.3 + v * 0.7, 0.9 + v * 0.4, 0.05);
  };

  const pocket = (speed: number): void => {
    const v = level(speed, 3);
    play('pot', 0.6 + v * 0.4, 0.97 + v * 0.12, 0.04);
  };

  prefetch(); // 生成と同時にネットワーク先読みを開始
  return { resume, cue, collision, pocket };
};
